export type Constructors<T> = { [K in keyof T]: Constructor<T[K]> };

type Constructor<C> = C extends string
  ? StringConstructor
  : C extends number
    ? NumberConstructor
    : never;

/**
 * Type-level conversion from snake_case to camelCase
 */
type SnakeToCamelCase<S extends string> = S extends `_${infer Rest}`
  ? SnakeToCamelCase<Rest>
  : S extends `${infer T}_${infer U}`
    ? `${T}${Capitalize<SnakeToCamelCase<U>>}`
    : S;

/**
 * Type-level conversion from camelCase to snake_case
 */
type CamelToSnakeCase<S extends string> = SnakeChars<S, "", "">;

/**
 * Walks the camelCase name one character at a time, where `Prev` is the
 * character already consumed and `Acc` the snake_case built so far. An
 * uppercase run of two or more — an acronym — survives intact, mirroring
 * `stringToSnake`.
 */
type SnakeChars<
  S extends string,
  Prev extends string,
  Acc extends string,
> = S extends `${infer C}${infer Rest}`
  ? SnakeChars<Rest, C, `${Acc}${SnakeChar<C, Prev, Rest>}`>
  : Acc;

type SnakeChar<C extends string, Prev extends string, Rest extends string> =
  IsUpper<C> extends true ? `${WordSeparator<Prev, Rest>}${UpperChar<C, Prev, Rest>}` : C;

/** An uppercase character opens a new word unless it continues an acronym. */
type WordSeparator<Prev extends string, Rest extends string> = Prev extends ""
  ? ""
  : StartsLower<Prev> extends true
    ? "_"
    : StartsLower<Rest> extends true
      ? "_"
      : "";

/**
 * Kept uppercase while it belongs to an acronym of two or more, lowercased
 * otherwise — like `stringToSnake`, where an uppercase run followed by a
 * lowercase letter surrenders its last character to the next word.
 */
type UpperChar<C extends string, Prev extends string, Rest extends string> =
  IsUpper<Prev> extends true
    ? StartsUpper<Rest> extends true
      ? C
      : Rest extends ""
        ? C
        : Lowercase<C>
    : StartsUpper<Rest> extends true
      ? StartsUpperLower<Rest> extends true
        ? Lowercase<C>
        : C
      : Lowercase<C>;

/** The next word begins: an uppercase letter directly followed by a lowercase one. */
type StartsUpperLower<S extends string> = S extends `${infer A}${infer B}${string}`
  ? IsUpper<A> extends true
    ? IsUpper<B> extends true
      ? false
      : true
    : false
  : false;

type StartsUpper<S extends string> = S extends `${infer C}${string}` ? IsUpper<C> : false;

type StartsLower<S extends string> = S extends `${infer C}${string}`
  ? IsUpper<C> extends true
    ? false
    : true
  : false;

type IsUpper<C extends string> =
  C extends Uppercase<C> ? (C extends Lowercase<C> ? false : true) : false;

/**
 * Recursively transforms all keys in an object from snake_case to camelCase at the type level
 */
export type SnakeToCamel<T> =
  T extends Array<infer U>
    ? Array<SnakeToCamel<U>>
    : T extends object
      ? {
          [
            K in keyof T as K extends string ? (string extends K ? K : SnakeToCamelCase<K>) : K
          ]: SnakeToCamel<T[K]>;
        }
      : T;

/**
 * Recursively transforms all keys in an object from camelCase to snake_case at the type level
 */
export type CamelToSnake<T> =
  T extends Array<infer U>
    ? Array<CamelToSnake<U>>
    : T extends object
      ? {
          [
            K in keyof T as K extends string ? (string extends K ? K : CamelToSnakeCase<K>) : K
          ]: CamelToSnake<T[K]>;
        }
      : T;

/**
 * Recursively transforms object keys from snake_case to camelCase
 *
 * This utility is used to transform data received from the backend (snake_case)
 * to match TypeScript interface expectations (camelCase) before Zod validation.
 *
 * The return type accurately reflects the transformed keys at the type level,
 * providing full type safety.
 *
 * @param input - The data to transform (object, array, or primitive)
 * @returns The transformed data with camelCase keys (typed correctly)
 */
export function keysToCamel<S>(input: S): SnakeToCamel<S> {
  return transform(input, stringToCamel) as SnakeToCamel<S>;
}

/**
 * Recursively transforms object keys from camelCase to snake_case
 *
 * This utility is used to transform data being sent to the backend (camelCase)
 * to match backend expectations (snake_case).
 *
 * The return type accurately reflects the transformed keys at the type level,
 * providing full type safety.
 *
 * @param input - The data to transform (object, array, or primitive)
 * @returns The transformed data with snake_case keys (typed correctly)
 */
export function keysToSnake<C>(input: C): CamelToSnake<C> {
  return transform(input, stringToSnake) as CamelToSnake<C>;
}

function transform<T>(input: T, keyFunc: (key: string) => string): T {
  if (input === null || input === undefined || typeof input !== "object") {
    return input;
  }

  if (Array.isArray(input)) {
    return input.map((item) => transform(item, keyFunc)) as T;
  }

  const record = input as Record<string, unknown>;
  const result: Record<string, unknown> = {};
  for (const key in record) {
    if (Object.prototype.hasOwnProperty.call(record, key)) {
      result[keyFunc(key)] = transform(record[key], keyFunc);
    }
  }
  return result as T;
}

const ACRONYM = /^[A-Z]{2,}$/;

export function stringToCamel(str: string): string {
  if (!str.includes("_")) return str;
  return str
    .split("_")
    .filter((part) => part.length > 0)
    .map((part, i) => {
      if (ACRONYM.test(part)) return part;
      const lower = part.toLowerCase();
      return i === 0 ? lower : lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join("");
}

export function stringToSnake(str: string): string {
  return str
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1_$2")
    .split("_")
    .map((part) => (ACRONYM.test(part) ? part : part.toLowerCase()))
    .join("_");
}
